import React, { Suspense, lazy, useEffect, useLayoutEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { trackPageView } from "./hooks/usePageTracking";

// Layouts - marketing layout eager (every public page), dashboard layout lazy (admin only)
import MarketingLayout from "./components/layout/MarketingLayout";
const DashboardLayout = lazy(() => import("./components/layout/DashboardLayout"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-600"></div>
  </div>
);

// Public Pages - Lazy loaded
const Home = lazy(() => import("./pages/Home"));
const Solutions = lazy(() => import("./pages/Solutions"));
const SolutionDetail = lazy(() => import("./pages/SolutionDetail"));
const About = lazy(() => import("./pages/About"));
const Insights = lazy(() => import("./pages/Insights"));
const InsightDetail = lazy(() => import("./pages/InsightDetail"));
const Impact = lazy(() => import("./pages/Impact"));
const Contact = lazy(() => import("./pages/Contact"));
const Workshops = lazy(() => import("./pages/Workshops"));
const WorkshopDetail = lazy(() => import("./pages/WorkshopDetail"));
const CaseStudyDetail = lazy(() => import("./pages/CaseStudyDetail"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const AIIndex = lazy(() => import("./pages/AIIndex"));
const HowToReferenceUs = lazy(() => import("./pages/HowToReferenceUs"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Retreats = lazy(() => import("./pages/Retreats"));
const TeamBuildingQuiz = lazy(() => import("./pages/TeamBuildingQuiz"));
const TransformationDiagnostic = lazy(() => import("./pages/TransformationDiagnostic"));
const BlueWaveGo = lazy(() => import("./pages/BlueWaveGo"));
const ECDCertification = lazy(() => import("./pages/ECDCertification"));
const ECDBonaire = lazy(() => import("./pages/ECDBonaire"));
const ECDLeadership = lazy(() => import("./pages/ECDLeadership"));
const ECDStakeholder = lazy(() => import("./pages/ECDStakeholder"));
const ECDCourses = lazy(() => import("./pages/ECDCourses"));
const CulturalTransformationTools = lazy(() => import("./pages/CulturalTransformationTools"));
const CreativeCatalyst = lazy(() => import("./pages/CreativeCatalyst"));
const WorkshopMeeting = lazy(() => import("./pages/WorkshopMeeting"));
const Tools = lazy(() => import("./pages/Tools"));
const Careers = lazy(() => import("./pages/Careers"));
const VacancyDetail = lazy(() => import("./pages/VacancyDetail"));
const MyTools = lazy(() => import("./pages/MyTools"));
const ToolDetail = lazy(() => import("./pages/ToolDetail"));
const GameDetail = lazy(() => import("./pages/GameDetail"));
const PaymentReturn = lazy(() => import("./pages/PaymentReturn"));
const PaymentDetails = lazy(() => import("./pages/PaymentDetails"));

// Auth Pages - Lazy loaded
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));

// Admin Pages - Lazy loaded (only load when admin accesses)
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminSolutions = lazy(() => import("./pages/admin/Solutions"));
const AdminInsights = lazy(() => import("./pages/admin/Insights"));
const AdminTeam = lazy(() => import("./pages/admin/Team"));
const AdminTestimonials = lazy(() => import("./pages/admin/Testimonials"));
const AdminCases = lazy(() => import("./pages/admin/Cases"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminWorkshops = lazy(() => import("./pages/admin/Workshops"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminGames = lazy(() => import("./pages/admin/Games"));
const AdminClientLogos = lazy(() => import("./pages/admin/ClientLogos"));
const AdminPageHeaders = lazy(() => import("./pages/admin/PageHeaders"));
const AdminSeoSharing = lazy(() => import("./pages/admin/SeoSharing"));
const AdminLegalPages = lazy(() => import("./pages/admin/LegalPages"));
const AdminTools = lazy(() => import("./pages/admin/Tools"));
const AdminVacancies = lazy(() => import("./pages/admin/Vacancies"));
const AdminPayments = lazy(() => import("./pages/admin/Payments"));
const AdminContacts = lazy(() => import("./pages/admin/Contacts"));
const AdminHowWeWork = lazy(() => import("./pages/admin/HowWeWork"));
const AdminTypewriterQuestions = lazy(() => import("./pages/admin/TypewriterQuestions"));
const AdminPopup = lazy(() => import("./pages/admin/Popup"));
const SentooTestDashboard = lazy(() => import("./pages/admin/SentooTestDashboard"));

// Protected Route Component
import ProtectedRoute from "./components/ProtectedRoute";

// Cookie Consent
import { CookieConsent } from "./components/ui/cookie-consent";
import { FacebookPixel } from "./components/FacebookPixel";
import { initAnalyticsConsent, initContactClickTracking } from "./lib/analytics";
import { initLinkPrefetch } from "./lib/prefetch";

// App Router with session_id detection and page tracking
function AppRouter() {
  const location = useLocation();

  // Prefetch page chunks on link hover/touch so navigation feels instant
  useEffect(() => {
    initLinkPrefetch();
  }, []);

  // Always open new pages at the top (instant, so CSS smooth-scroll can't be interrupted)
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);

  // Track page views for lead scoring (only public pages)
  useEffect(() => {
    // Don't track admin pages
    if (!location.pathname.startsWith('/admin')) {
      trackPageView(location.pathname, document.title);
    }
  }, [location.pathname]);

  // Check URL fragment for session_id (from Emergent Auth)
  // This must be synchronous during render to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={<PageLoader />}>
        <AuthCallback />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Marketing Pages with Layout */}
        <Route element={<MarketingLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/solutions/:slug" element={<SolutionDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/:slug" element={<InsightDetail />} />
          <Route path="/impact" element={<Impact />} />
          <Route path="/impact/:slug" element={<CaseStudyDetail />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/workshops" element={<Workshops />} />
          <Route path="/workshops/:slug" element={<WorkshopDetail />} />
          <Route path="/workshops/:slug/meeting" element={<WorkshopMeeting />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/ai-index" element={<AIIndex />} />
          <Route path="/how-to-reference-us" element={<HowToReferenceUs />} />
          <Route path="/retreats" element={<Retreats />} />
          <Route path="/team-building-quiz" element={<TeamBuildingQuiz />} />
          <Route path="/transformation-diagnostic" element={<TransformationDiagnostic />} />
          <Route path="/go-team-blue-wave" element={<BlueWaveGo />} />
          <Route path="/ecd-certification" element={<ECDCertification />} />
          <Route path="/ecd-certification-bonaire" element={<ECDBonaire />} />
          <Route path="/ecd-leadership" element={<ECDLeadership />} />
          <Route path="/ecd-stakeholder" element={<ECDStakeholder />} />
          <Route path="/ecd-courses" element={<ECDCourses />} />
          <Route path="/cultural-transformation-tools" element={<CulturalTransformationTools />} />
          <Route path="/creative-catalyst" element={<CreativeCatalyst />} />
          <Route path="/tools" element={<Tools />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/careers/:slug" element={<VacancyDetail />} />
          <Route path="/my-tools" element={<MyTools />} />
          <Route path="/tools/:slug" element={<ToolDetail />} />
          <Route path="/solutions/team-building/:slug" element={<GameDetail />} />
          <Route path="/payment/return" element={<PaymentReturn />} />
          <Route path="/intpay" element={<PaymentDetails />} />
        </Route>

        {/* Admin Login (no layout) */}
        <Route path="/admin" element={<AdminLogin />} />

        {/* Admin Dashboard Pages (protected) */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="solutions" element={<AdminSolutions />} />
          <Route path="insights" element={<AdminInsights />} />
          <Route path="team" element={<AdminTeam />} />
          <Route path="testimonials" element={<AdminTestimonials />} />
          <Route path="cases" element={<AdminCases />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="workshops" element={<AdminWorkshops />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="games" element={<AdminGames />} />
          <Route path="client-logos" element={<AdminClientLogos />} />
          <Route path="page-headers" element={<AdminPageHeaders />} />
          <Route path="seo-sharing" element={<AdminSeoSharing />} />
          <Route path="legal-pages" element={<AdminLegalPages />} />
          <Route path="contacts" element={<AdminContacts />} />
          <Route path="how-we-work" element={<AdminHowWeWork />} />
          <Route path="typewriter-questions" element={<AdminTypewriterQuestions />} />
          <Route path="popup" element={<AdminPopup />} />
          <Route path="tools" element={<AdminTools />} />
          <Route path="vacancies" element={<AdminVacancies />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="test-sentoo" element={<SentooTestDashboard />} />
        </Route>

        {/* 404 Page */}
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    initAnalyticsConsent();
    initContactClickTracking();
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <FacebookPixel />
        <AppRouter />
        <CookieConsent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
